<script lang="ts">
  /**
   * Lyktan rapport-vy (S-08-04, omarbetad i S-04-16).
   *
   * Visar lista, filter och detaljvy för en vald rapport. Hard-rules
   * från UI-kontrakt vy 8:
   *  - Rapportör-identitet (user_id) exponeras INTE — vi listar bara
   *    plats, kategori, status, tid.
   *  - Inga fritextfält. Status-uppdateringar går via fasta motiverings-
   *    listor (REJECT_REASONS, REOPEN_REASONS) plus hårdkodad Eko.
   *  - Cookie-baserad auth — `credentials: 'include'` på alla anrop.
   *  - 401/403 -> tvingar ut till LoginView (via onUnauthorized-prop).
   */
  import { listReports, type AdminReport } from './reportsClient';
  import {
    ALL_CATEGORIES,
    ALL_STATUSES,
    adminStatusLabel,
    categoryLabel,
    type ReportCategory,
    type ReportStatus,
  } from './reportLabels';
  import {
    applyFilter,
    defaultFilterState,
    toggleCategory,
    toggleStatus,
    type ReportFilterState,
  } from './reportsState';
  import ReportDetail from './ReportDetail.svelte';

  type Props = {
    onUnauthorized: () => void;
  };

  const { onUnauthorized }: Props = $props();

  let filter: ReportFilterState = $state(defaultFilterState());
  let reports: AdminReport[] = $state([]);
  let loading = $state(false);
  let loadError: string | null = $state(null);
  let selectedId: string | null = $state(null);

  const filteredReports = $derived(applyFilter(reports, filter));
  const selectedReport = $derived(reports.find((r) => r.id === selectedId) ?? null);

  async function loadAll(): Promise<void> {
    loading = true;
    loadError = null;
    try {
      // Hämta brett — backend stöder bara en status / kategori i taget,
      // och vi vill ha multi-select. Listan har max 200 rader och
      // pilotskalan i Hisings Backa är liten.
      const result = await listReports({ limit: 200, offset: 0 });
      if (result.kind === 'unauthorized') {
        onUnauthorized();
        return;
      }
      if (result.kind === 'failure') {
        loadError = result.message;
        return;
      }
      reports = result.items;
    } finally {
      loading = false;
    }
  }

  function toggleCat(category: ReportCategory): void {
    filter = { ...filter, categories: toggleCategory(filter.categories, category) };
  }
  function toggleStat(status: ReportStatus): void {
    filter = { ...filter, statuses: toggleStatus(filter.statuses, status) };
  }
  function setFromDate(value: string): void {
    filter = { ...filter, fromDate: value === '' ? null : value };
  }
  function setToDate(value: string): void {
    filter = { ...filter, toDate: value === '' ? null : value };
  }
  function selectReport(id: string): void {
    selectedId = id;
  }
  function closeDetail(): void {
    selectedId = null;
  }
  function onReportUpdated(updated: AdminReport): void {
    reports = reports.map((r) => (r.id === updated.id ? updated : r));
  }

  // Auto-load vid montering. Använd $effect för Svelte 5-runor-API.
  $effect(() => {
    loadAll();
  });
</script>

<section class="reports" aria-label="Inkommen rapportering">
  <header>
    <div class="title-row">
      <button
        type="button"
        class="icon-btn icon-btn-secondary"
        onclick={loadAll}
        disabled={loading}
        aria-label={loading ? 'Laddar' : 'Uppdatera lista'}
        title={loading ? 'Laddar…' : 'Uppdatera'}
      >
        <svg
          viewBox="0 0 24 24"
          aria-hidden="true"
          stroke="currentColor"
          stroke-width="2"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          class:spinning={loading}
        >
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </svg>
      </button>
    </div>
  </header>

  <div class="filters" role="group" aria-label="Filter">
    <fieldset>
      <legend>Status</legend>
      {#each ALL_STATUSES as status (status)}
        <label class="chip">
          <input
            type="checkbox"
            checked={filter.statuses.has(status)}
            onchange={() => toggleStat(status)}
          />
          <span>{adminStatusLabel(status)}</span>
        </label>
      {/each}
    </fieldset>

    <fieldset>
      <legend>Kategori</legend>
      {#each ALL_CATEGORIES as category (category)}
        <label class="chip">
          <input
            type="checkbox"
            checked={filter.categories.has(category)}
            onchange={() => toggleCat(category)}
          />
          <span>{categoryLabel(category)}</span>
        </label>
      {/each}
    </fieldset>

    <fieldset class="dates">
      <legend>Datumintervall</legend>
      <label>
        <span>Från</span>
        <input
          type="date"
          value={filter.fromDate ?? ''}
          onchange={(e) => setFromDate((e.currentTarget as HTMLInputElement).value)}
        />
      </label>
      <label>
        <span>Till</span>
        <input
          type="date"
          value={filter.toDate ?? ''}
          onchange={(e) => setToDate((e.currentTarget as HTMLInputElement).value)}
        />
      </label>
    </fieldset>
  </div>

  {#if loadError}
    <p class="alert error" role="alert">{loadError}</p>
  {/if}

  <p class="result-count" aria-live="polite">
    {#if loading}
      Hämtar rapporter…
    {:else}
      Visar {filteredReports.length} av {reports.length} rapporter
    {/if}
  </p>

  <ul class="report-list">
    {#each filteredReports as report (report.id)}
      <li>
        <button
          type="button"
          class="row"
          class:selected={report.id === selectedId}
          onclick={() => selectReport(report.id)}
        >
          <span class="cat">{categoryLabel(report.category)}</span>
          <span class="time">{report.reportedAt}</span>
          <span class="status status-{report.status}">{adminStatusLabel(report.status)}</span>
        </button>
      </li>
    {/each}
    {#if !loading && filteredReports.length === 0 && reports.length > 0}
      <li class="empty">Inga rapporter matchar filtret.</li>
    {:else if !loading && reports.length === 0 && !loadError}
      <li class="empty">Inga rapporter har skickats in ännu.</li>
    {/if}
  </ul>

  {#if selectedReport}
    <ReportDetail
      report={selectedReport}
      onUpdated={onReportUpdated}
      {onUnauthorized}
      onClose={closeDetail}
    />
  {/if}
</section>

<style>
  .reports {
    display: grid;
    gap: 1rem;
  }
  header {
    display: grid;
    gap: 0.4rem;
  }
  .eyebrow {
    font-size: 0.85rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--accent);
    font-weight: 600;
    margin: 0;
  }
  .title-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid var(--rule);
    border-radius: 4px;
    color: var(--text-dim);
    cursor: pointer;
    width: 2.4rem;
    height: 2.4rem;
    padding: 0.45rem;
    font-family: inherit;
    transition: color 120ms ease, border-color 120ms ease, background 120ms ease;
  }
  .icon-btn svg { width: 100%; height: 100%; display: block; }
  .icon-btn:hover:not(:disabled) { color: var(--text); border-color: var(--text-dim); }
  .icon-btn:focus-visible { outline: 2px solid var(--glow); outline-offset: 2px; }
  .icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .icon-btn-secondary { color: var(--accent); border-color: var(--accent); }
  .icon-btn-secondary:hover:not(:disabled) { background: var(--accent-soft); color: var(--glow); }
  .spinning { animation: icon-spin 900ms linear infinite; }
  @keyframes icon-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
  .filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 1rem;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 1rem;
  }
  fieldset {
    border: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 0.4rem;
  }
  legend {
    font-size: 0.78rem;
    color: var(--text-dim);
    margin-bottom: 0.25rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
  }
  .chip {
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    cursor: pointer;
    font-size: 0.9rem;
    color: var(--text);
  }
  .chip:focus-within {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
    border-radius: 2px;
  }
  .dates label {
    display: grid;
    gap: 0.25rem;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .dates input {
    background: var(--bg);
    color: var(--text);
    border: 1px solid var(--rule);
    border-radius: 4px;
    padding: 0.4rem 0.6rem;
    font: inherit;
    color-scheme: dark;
  }
  .dates input:focus {
    outline: 2px solid var(--accent);
    outline-offset: 0;
    border-color: var(--accent);
  }
  .alert {
    padding: 0.6rem 0.85rem;
    border-radius: 4px;
    margin: 0;
    font-size: 0.9rem;
    border: 1px solid var(--rule);
  }
  .alert.error {
    color: var(--danger);
    background: var(--danger-soft);
    border-color: var(--danger);
  }
  .result-count {
    margin: 0;
    font-size: 0.85rem;
    color: var(--text-dim);
  }
  .report-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    gap: 0.4rem;
  }
  .row {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr;
    gap: 0.75rem;
    width: 100%;
    text-align: left;
    background: var(--bg-elev);
    border: 1px solid var(--rule);
    color: var(--text);
    border-radius: 4px;
    padding: 0.7rem 0.9rem;
    cursor: pointer;
    font: inherit;
    font-size: 0.9rem;
  }
  .row:hover {
    border-color: var(--accent);
  }
  .row.selected {
    border-color: var(--accent);
    background: var(--accent-soft);
    box-shadow: 0 0 0 1px var(--accent);
  }
  .cat {
    font-weight: 500;
  }
  .time {
    color: var(--text-dim);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.8rem;
  }
  .status {
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
    font-weight: 600;
    align-self: center;
    text-align: right;
  }
  .status-new {
    color: var(--glow);
  }
  .status-handled {
    color: var(--accent);
  }
  .status-not_actionable {
    color: var(--text-dim);
  }
  .empty {
    background: var(--bg-elev);
    border: 1px dashed var(--rule);
    border-radius: 4px;
    padding: 1rem;
    text-align: center;
    color: var(--text-dim);
    font-size: 0.9rem;
  }
</style>
